// import { ThemeEvents, MediaStartedPlayingEvent } from 'pent-core/events.js';
import { PentEvents, dispatchMediaStartedPlaying } from "./pent-core/events.js"
// import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class PlDeferredMedia extends HTMLElement {
  /** @type {boolean} */
  isPlaying = false

  #abortController = new AbortController()

  connectedCallback() {
    const signal = this.#abortController.signal

    // Pause this media when another media starts
    document.addEventListener(
      "media:started-playing",
      (e) => {
        if (e?.detail?.resource === this) return // don't pause yourself
        this.pauseMedia()
      },
      { signal }
    )

    // Wire up poster play button
    const playBtn = this.querySelector('[on\\:click="/showDeferredMedia"]')
    if (playBtn) {
      playBtn.addEventListener("click", this.showDeferredMedia, { signal })
    }

    // Wire up toggle play/pause button (when controls are disabled)
    const toggleBtn = this.querySelector('[on\\:click="/toggleMedia"]')
    if (toggleBtn) {
      toggleBtn.addEventListener("click", () => this.toggleMedia(), { signal })
    }
  }

  disconnectedCallback() {
    this.#abortController.abort()
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.querySelector('[ref="toggleMediaButton"]')
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove("pl--hidden")
      const playIcon = toggleMediaButton.querySelector(".pl--icon-play")
      if (playIcon) playIcon.classList.toggle("pl--hidden", isPlaying)
      const pauseIcon = toggleMediaButton.querySelector(".pl--icon-pause")
      if (pauseIcon) pauseIcon.classList.toggle("pl--hidden", !isPlaying)
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true)
    this.isPlaying = true
    this.updatePlayPauseHint(this.isPlaying)
  }

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute("data-media-loaded")) return

    dispatchMediaStartedPlaying(this, { resource: this })

    const content = this.querySelector("template")?.content.firstElementChild?.cloneNode(true)
    if (!content) return

    this.setAttribute("data-media-loaded", "true")
    this.appendChild(content)

    if (focus && content instanceof HTMLElement) content.focus()

    const posterBtn = this.querySelector('[ref="deferredMediaPlayButton"]')
    if (posterBtn) posterBtn.classList.add("pl--deferred-media__playing")

    if (content instanceof HTMLVideoElement && content.getAttribute("autoplay")) {
      content.play()
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia()
    } else {
      this.playMedia()
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]")
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        "*"
      )
    } else {
      this.querySelector("video")?.play()
    }
    this.isPlaying = true
    this.updatePlayPauseHint(this.isPlaying)
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]")

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"' + "pauseVideo" + '","args":""}'
          : '{"method":"pause"}',
        "*"
      )
    } else {
      this.querySelector("video")?.pause()
    }
    this.isPlaying = false

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute("data-media-loaded")) {
      this.updatePlayPauseHint(this.isPlaying)
    }
  }
}

if (!customElements.get("pl-deferred-media")) {
  customElements.define("pl-deferred-media", PlDeferredMedia)
}
